import type { TCheckResult } from '@conversed/rest/src/contracts/llm-usages/check-results';
import type {
  ConversationsViewType,
  TConversationsResponse,
} from '@conversed/rest/src/contracts/llm-usages/llm-usages.contract';
import type { SortOption } from '@conversed/rest/src/contracts/llm-usages/sort-option.enum';
import type { TLLMUsageTranslation } from '@conversed/shared/src/services/llm/llm.type';
import type { useModal } from '@hooks/modal/useModal';

type GetConversationsByWeeksType = (
  weeks: number[],
  configId: string,
  year: number,
  llmBlockId: string,
  onlyUnverifiedAnswers: boolean,
  sortOption?: SortOption,
  includeNotEvaluable?: boolean,
  viewType?: ConversationsViewType,
  page?: number,
  perPage?: number,
  filterKeywords?: string[],
  hotTopicId?: string,
) => Promise<TConversationsResponse>;

export async function showFactCheckingModalByData(
  selectedRows: number[],
  selectedConfigId: string,
  year: number,
  llmBlockId: string,
  totalCheckedItems: number,
  totalItems: number,
  showModal: ReturnType<typeof useModal>['showModal'],
  getConversationsByWeeks: GetConversationsByWeeksType,
  callback: () => void,
  updatedConversation?: (checkResult: TCheckResult) => void,
  hotTopicId?: string,
): Promise<void> {
  // If there are no items, then no modals are needed.
  if (totalItems === 0) {
    return;
  }

  // If there is only one item and it is NOT verified, then only the Fact Checking modal is needed, as the Prestart modal is not required.
  if (totalItems === 1 && totalCheckedItems === 0) {
    const data = await getConversationsByWeeks(
      selectedRows,
      selectedConfigId,
      year,
      llmBlockId,
      false,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      hotTopicId,
    );

    showModal('train-ai-fact-checking', '', {
      data,
      updatedConversation,
      callback, // After the Fact Checking modal is closed, the callback function is called. This is will trigger an API call and refresh the table.
      requestedHotTopicId: hotTopicId,
    });

    return;
  }

  showModal('train-ai-fact-checking-prestart', '', {
    selectedWeeks: selectedRows,
    selectedConfigId,
    year,
    llmBlockId,
    totalCheckedItems,
    totalItems,
    updatedConversation,
    callback, // Use the callback function to the Fact Checking modal, after selecting the filter value then the Fact Checking modal will open.
    // After the Fact Checking modal is closed, the callback function is called. This will trigger an API call and refresh the table.
    requestedHotTopicId: hotTopicId,
  });
}

export type TTranslationFields = {
  translation?: TLLMUsageTranslation;
};

export const updateCheckResultFieldInState = (
  setConversations: React.Dispatch<
    React.SetStateAction<TConversationsResponse>
  >,
  conversationId: string,
  checkResult: TCheckResult,
  isReoptimization = false,
  _requestedLLMBlockId?: string,
  onComplete?: (updatedCount: number) => void,
  translationFields?: TTranslationFields,
): void => {
  setConversations((prev) => {
    const evaluatedConversation = prev.results.find(
      (item) => item._id === conversationId,
    );

    let updatedCount = 0;

    const updatedConversations = prev.results.map((item) => {
      // Handle the evaluated conversation
      if (item._id === conversationId) {
        // Only count if it wasn't previously evaluated
        if (!item.checkResult || !item.checkResult.status) {
          updatedCount++;
        }
        return {
          ...item,
          ...(translationFields ?? {}),
          checkResult: {
            status: checkResult.status,
            correctedAnswer: checkResult.correctedAnswer,
            correctedURL: checkResult.correctedURL,
            checkedBy: checkResult.checkedBy,
            checkedByName: checkResult?.checkedByName || '',
            checkedAt: checkResult?.checkedAt || '',
            reportIssue: checkResult.reportIssue,
            reportIssueParams: checkResult.reportIssueParams,
            useForAIKnowledgeData: checkResult.useForAIKnowledgeData,
          },
          isEvaluable: true,
        };
      }

      // Handle other conversations with the same question
      if (
        checkResult.status &&
        item.question?.toLowerCase()?.trim() ===
          evaluatedConversation?.question?.toLowerCase()?.trim()
      ) {
        // Only count if it wasn't previously evaluated AND it's currently evaluable
        if (
          item.isEvaluable &&
          (!item.checkResult || !item.checkResult.status)
        ) {
          updatedCount++;
        }

        if (isReoptimization) {
          // The core returns `clearedCheckResultDocId` to identify the exact
          // sibling document whose checkResult it removed (the one that held
          // the primary evaluation and had no remaining primaries in other
          // contexts after the reoptimization). Use this directly rather than
          // applying heuristics, since the frontend's `evaluatedIn` state is
          // stale and cannot reliably replicate the core's decision.
          if (
            checkResult.clearedCheckResultDocId &&
            checkResult.clearedCheckResultDocId === item._id
          ) {
            return {
              ...item,
              isEvaluable: false,
              checkResult: undefined,
            };
          }

          // All other same-question items: mark as non-evaluable but preserve
          // their checkResult — the core kept it because they still have
          // primary evaluations in other block-specific contexts.
          return {
            ...item,
            isEvaluable: false,
          };
        }

        // In normal mode, just mark as not evaluable to prevent duplicate evaluation
        return {
          ...item,
          isEvaluable: false,
        };
      }

      return item;
    });

    // Call the callback with the actual count
    if (onComplete) {
      setTimeout(() => onComplete(updatedCount), 0);
    }

    return {
      ...prev,
      results: updatedConversations,
    };
  });
};
